import { CLASS_NAMES, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the TwoPie module
 */
export default class StatsPie extends MessageBus {
  /**
   * Constructor function of the TwoPie class. Must receive one argument, a DOM
   * node representing the container for the pie charts.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} TwoPie A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} TwoPie A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.pies = this.element.querySelectorAll(
      `.${CLASS_NAMES.TWO_UP_STATS_PIE}`
    );
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} TwoPie A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onCounterStart = this.onCounterStart.bind(this);
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} TwoPie A reference to the current instance of the class
   * @chainable
   */
  enable() {
    // Listen for custom event when counter starts
    this.element.addEventListener(
      'percentageCounterStart',
      this.onCounterStart
    );
    return this;
  }

  onCounterStart() {
    if (this.pies.length === 0) return;

    this.pies.forEach((pie) => {
      this.animatePie(pie);
    });
  }

  /**
   * Animate the pie chart
   * @param {HTMLElement} pie - The pie chart element to animate
   * @returns {void}
   */
  animatePie(pie) {
    const targetPct = parseInt(
      pie.getAttribute(`${SELECTORS.DATA_TARGET_PCT}`),
      10
    );
    const duration = 1500; // Animation duration in milliseconds
    const startTime = performance.now();

    const updatePie = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1); // Ensure progress doesn't exceed 1
      const currentPct = Math.floor(progress * targetPct);

      // Update the pie chart
      pie.setAttribute('data-pct', `${currentPct}%`);

      const arc = pie.querySelector(`${SELECTORS.SVG_CIRCLE_NTH_CHILD}`);
      if (arc) {
        const circumference = 271.747; // Circumference of the circle
        const dashLength = (circumference / 100) * currentPct;
        arc.style.strokeDasharray = `${dashLength} ${
          circumference - dashLength
        }`;
      }

      // Continue animation until progress is complete
      if (progress < 1) {
        requestAnimationFrame(updatePie);
      }
    };

    // Start the animation
    requestAnimationFrame(updatePie);

    return this;
  }
}
